import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngrx/store";
import { ButtonModule } from "primeng/button";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { MenuItem, SelectItem } from "primeng/api";
import { AvatarModule } from "primeng/avatar";
import { AvatarGroupModule } from "primeng/avatargroup";
import { TooltipModule } from "primeng/tooltip";
import { SelectModule } from "primeng/select";
import { IftaLabelModule } from "primeng/iftalabel";
import { RootState } from "../../../common/ngrx/root.model";
import { AuthService } from "../../../common/services/auth.service";
import { MenuModule } from "primeng/menu";
import { AppActions } from "../../../common/ngrx/app/app.reducer";
import { environment } from "../../../../environments/environment";
import { appDarkModeClass } from "../../../app.theme";
import { DataService } from "../../../common/services/data.service";
import { VigilantApiDtosOrgsGetResponse } from "../../../common/api/vigilant/orgs/orgs.model";
import { AppTheme, AppRootOrgFilterToken } from "../../../common/constants/session.constants";

@UntilDestroy()
@Component({
  standalone: true,
  selector: "vig-header",
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
  imports: [
    FormsModule,
    ButtonModule,
    BreadcrumbModule,
    AvatarModule,
    AvatarGroupModule,
    TooltipModule,
    MenuModule,
    SelectModule,
    IftaLabelModule,
  ],
})
export class HeaderComponent implements OnInit {
  constructor(
    private store: Store<RootState>,
    private authService: AuthService,
    private dataService: DataService
  ) {}

  _apiVersion: string | undefined;
  _breadcrumbs: string[] = [];
  _userLoggedIn = false;
  _userEmail: string | undefined;
  _userSuperAdmin: boolean | undefined;
  _orgs: VigilantApiDtosOrgsGetResponse[] | undefined;

  darkMode = false;
  dataInitialized = false;
  breadcrumbHome: MenuItem = { icon: "pi pi-home", routerLink: "/admin/home" };
  breadcrumbItems: MenuItem[] = [];
  avatarLabel: string | undefined;
  avatarTooltip: string | undefined;
  avatarIcon: string | undefined;
  avatarActions: MenuItem[] | undefined;
  rootOrgOptions: SelectItem[] = [];
  rootOrgOption: number | undefined;

  ngOnInit(): void {
    this.initializeStore();
    this.buildActionItems();
    this.checkAppTheme();

    const rootOrgOption = sessionStorage.getItem(AppRootOrgFilterToken);

    if (rootOrgOption) {
      this.rootOrgOption = parseInt(rootOrgOption, 10);
      this.store.dispatch(AppActions.SetRootOrgOption({ rootOrgOption: this.rootOrgOption ?? undefined }));
    }
  }

  initializeStore(): void {
    this.store.pipe(untilDestroyed(this)).subscribe((state: RootState) => {
      if (state.app.userLoggedIn && !this.dataInitialized) {
        this.dataInitialized = true;
        this.dataService.initializeData(["version", "orgs"]);
      }

      this._apiVersion = state.app.apiVersion;
      this._breadcrumbs = state.app.breadcrumbs;
      this._userLoggedIn = state.app.userLoggedIn;
      this._userSuperAdmin = state.app.userSuperAdmin;
      this._userEmail = state.app.userEmail;
      this._orgs = state.orgs.orgs;

      if (state.app.breadcrumbs.length > 0) {
        this.breadcrumbItems = state.app.breadcrumbs.map((breadcrumb) => {
          return { label: breadcrumb };
        });

        document.title = `Vigilant Admin | ${state.app.breadcrumbs.at(-1)}`;
      } else {
        this.breadcrumbItems = [];
        document.title = `Vigilant Admin`;
      }

      if (state.app.userFirstName && state.app.userLastName) {
        this.avatarLabel = `${state.app.userFirstName.charAt(0).toUpperCase()}${state.app.userLastName.charAt(0).toUpperCase()}`;
        this.avatarTooltip = `${state.app.userFirstName} ${state.app.userLastName}`;
        this.avatarIcon = undefined;
      } else {
        this.avatarLabel = undefined;
        this.avatarTooltip = undefined;
        this.avatarIcon = "pi pi-user";
      }

      if (this._orgs) {
        this.rootOrgOptions = this._orgs
          .filter((org) => !org.parentOrgId)
          .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
          .map((org) => {
            return {
              label: org.name,
              value: org.id,
            };
          });
      }
    });
  }

  buildActionItems(): void {
    const actions: MenuItem[] = [];

    actions.push(
      {
        label: "About",
        icon: "pi pi-info-circle",
        command: (): void => {
          this.store.dispatch(
            AppActions.OpenDialog({
              title: "About",
              message: `
              <div>
                <div class='mb-2 mr-6'>UI Version: <strong>${environment.buildVersion}</strong></div>
                <div class='mb-2 mr-6'>API Version: <strong>${this._apiVersion}</strong></div>
                <div class='mb-2 mr-6'>Environment: <strong>${environment.environmentName}</strong></div>
                <div class='mb-2 mr-6'>Member Email: <strong>${this._userEmail}</strong></div>
                ${this._userSuperAdmin ? "<div class='pt-2'><em>You have super administrator access.</em></div>" : ""}
              </div>`,
              modal: true,
            })
          );
        },
      },
      {
        label: "Logout",
        icon: "pi pi-sign-out",
        command: (): void => {
          this.authService.logout();
        },
      }
    );

    this.avatarActions = [
      {
        label: "Account",
        items: actions,
      },
    ];
  }

  checkAppTheme(): void {
    const appTheme = localStorage.getItem(AppTheme);

    if (appTheme === "dark") {
      this.darkMode = true;
    } else if (appTheme === "light") {
      this.darkMode = false;
    } else {
      if (window.matchMedia) {
        this.darkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
      } else {
        this.darkMode = false;
      }
    }

    if (window.matchMedia) {
      window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (event) => {
        const appTheme = localStorage.getItem(AppTheme);

        if (!appTheme) {
          this.darkMode = event.matches;
        }
      });
    }
  }

  onToggleDarkMode(): void {
    this.darkMode = !this.darkMode;

    if (this.darkMode) {
      localStorage.setItem(AppTheme, "dark");
      document.querySelector("html")?.classList.add(appDarkModeClass);
    } else {
      localStorage.setItem(AppTheme, "light");
      document.querySelector("html")?.classList.remove(appDarkModeClass);
    }
  }

  onRootOrgChange(): void {
    sessionStorage.setItem(AppRootOrgFilterToken, this.rootOrgOption?.toString() ?? "");
    this.store.dispatch(AppActions.SetRootOrgOption({ rootOrgOption: this.rootOrgOption ?? undefined }));
  }
}
